/**
 * Team Chat real-time connection diagnostics: a bounded, in-memory timeline of what the receive
 * path did, so a "connected but deaf" tab (the socket still looks online but no inbound event,
 * shared image or live-session frame arrives, and only a reload recovers) can be root-caused
 * without a crash to hang the investigation on.
 *
 * The ring buffer is the primary artifact. It is readable from the DevTools console at any time —
 * even when nothing threw — via the global accessor, which is how a no-crash deaf state is
 * exfiltrated from a user's tab:
 *
 *   copy(window.__teamHubDiagnostics())   // the whole timeline, oldest first, newest last
 *
 * Console mirroring is off unless a per-tab debug flag is set, so production consoles stay quiet:
 *
 *   localStorage.setItem('teamHub:debug', '1')   // persists across reloads; mirrors to console.debug
 *
 * When Sentry is initialized (every deployed environment), each entry is also added as a
 * breadcrumb, so the recent timeline rides along on any error report automatically — no extra
 * dependency, and a no-op when Sentry is not wired (local dev, unit tests).
 *
 * Nothing here logs message bodies or any user content: ids, sequence numbers, counts and state
 * names only.
 */
import * as Sentry from '@sentry/react';
/** How many entries the ring keeps. Enough to span a reconnect cycle and the minutes around it. */
export const TEAM_HUB_DIAG_LIMIT = 200;
let ring = [];
function now() {
    try {
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            return performance.now();
        }
    }
    catch {
        // performance unavailable (very old/edge runtimes): fall back to wall clock.
    }
    return Date.now();
}
/** High-resolution monotonic clock, shared with teamHub so deltas line up. */
export function teamHubClock() {
    return now();
}
function isDebugEnabled() {
    try {
        const flag = localStorage.getItem('teamHub:debug');
        return flag === '1' || flag === 'true';
    }
    catch {
        // Storage blocked (private window, disabled cookies): default to quiet.
        return false;
    }
}
/**
 * Appends one entry to the timeline. Must never throw into the hub, so every side effect
 * (buffer, console, Sentry) is guarded independently.
 */
export function recordTeamHubDiag(event, data) {
    const entry = { t: Math.round(now()), at: new Date().toISOString(), event };
    if (data !== undefined)
        entry.data = data;
    try {
        ring.push(entry);
        if (ring.length > TEAM_HUB_DIAG_LIMIT)
            ring.splice(0, ring.length - TEAM_HUB_DIAG_LIMIT);
    }
    catch {
        // Buffer failure must not break delivery.
    }
    if (isDebugEnabled()) {
        try {
            if (data !== undefined)
                console.debug('[teamHub]', event, data);
            else
                console.debug('[teamHub]', event);
        }
        catch {
            // console unavailable: ignore.
        }
    }
    try {
        Sentry.addBreadcrumb({ category: 'teamHub', level: 'info', message: event, data });
    }
    catch {
        // Sentry not wired, or breadcrumb rejected: ignore.
    }
}
export function makeDiagThrottle(minIntervalMs, clock = teamHubClock) {
    let lastKey = null;
    let lastAt = 0;
    let coalesced = 0;
    const throttle = ((key, event, data) => {
        const t = clock();
        if (key !== lastKey || t - lastAt >= minIntervalMs) {
            recordTeamHubDiag(event, coalesced > 0 ? { ...data, coalesced } : data);
            lastKey = key;
            lastAt = t;
            coalesced = 0;
            return true;
        }
        coalesced += 1;
        return false;
    });
    throttle.reset = () => {
        lastKey = null;
        lastAt = 0;
        coalesced = 0;
    };
    return throttle;
}
/** The whole timeline, oldest first and newest last, as a copy (mutating it cannot corrupt state). */
export function dumpTeamHubDiagnostics() {
    return ring.slice();
}
/** Empties the timeline. For tests, and for a console user who wants a clean capture window. */
export function clearTeamHubDiagnostics() {
    ring = [];
}
// The global dump accessor. A function (not the array) so the console always reads the live
// timeline, and so a user can call it at any moment to exfiltrate a no-crash deaf state.
if (typeof window !== 'undefined') {
    try {
        ;
        window.__teamHubDiagnostics = dumpTeamHubDiagnostics;
    }
    catch {
        // Read-only/locked window: the module exports still work for tests.
    }
}
